import { SagaIterator } from 'redux-saga';
import { call, put, select, takeEvery } from 'redux-saga/effects';
import { Messages } from '../../../codegen/Localize';
import { IGroupInfo } from '../../../ts/models/IGroupInfo';
import { ISiteUserInfo } from '../../../ts/models/ISiteUserInfo';
import { Set } from '../../../ts/models/Set';
import { ActiveDirectoryGroupAction } from '../../../ts/services/ActiveDirectoryGroupAction';
import { AddGroupsOptionsAction } from '../../../ts/services/AddGroupsOptionsAction';
import { AddUsersToGroupAction } from '../../../ts/services/AddUsersToGroupAction';
import { CreateGroupDialogAction } from '../../../ts/services/CreateGroupDialogAction';
import { DeleteGroupAction } from '../../../ts/services/DeleteGroupAction';
import { RemoveUsersFromGroupAction } from '../../../ts/services/RemoveUsersFromGroupAction';
import { ServerService } from '../../../ts/services/ServerService';
import { UpdateGroupsMinimumSiteRoleAction } from '../../../ts/services/UpdateGroupsMinimumSiteRoleAction';
import { UpdateLocalGroupGrantLicenseModeAction } from '../../../ts/services/UpdateLocalGroupGrantLicenseModeAction';
import { AddGroup, DeleteGroups, GroupAddUsers, GroupRemoveUsers, SyncAdGroup, SyncAdGroups, UpdateGroupsMinimumSiteRole, UpdateLocalGroupsLicenseUponLogin } from '../../actions/AppActionTypes';
import { requestFailed } from '../../actions/RequestFailedAction';
import { GroupAddAction, GroupMenuAction, GroupMinimumSiteRoleUpdateAction, GroupsDeleteAction, GroupSyncAction, GroupUserAddAction, LocalGroupLicenseUponLoginUpdateAction } from '../../actions/spaces/GroupMenuActions';
import { selectSpaceInfo } from '../../selectors/SpaceInfoSelectors';
import { ServiceLocator } from '../../ServiceLocator';
import { SpaceInfo } from '../../state/SpaceInfo';
import { reloadSpace } from './content/RefreshContentSagas';

export function addUsers(group: IGroupInfo) {
  return ServiceLocator.get<AddUsersToGroupAction>('AddUsersToGroupAction')
  .showAddUsersToGroupDialog(group);
}

export function* handleAddUsers(action: GroupUserAddAction): SagaIterator {
  try {
    yield call(addUsers, action.group);
    yield call(reloadSpace);
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function removeUsers(users: ISiteUserInfo[], spaceInfo: SpaceInfo) {
  return ServiceLocator.get<RemoveUsersFromGroupAction>('RemoveUsersFromGroupAction')
    .showRemoveUsersFromGroupDialog(Set.fromIdItemArray(users), spaceInfo.item as IGroupInfo);
}

export function* handleRemoveUsers(action: GroupMenuAction): SagaIterator {
  try {
    const spaceInfo = yield select(selectSpaceInfo);
    yield call(removeUsers, action.items, spaceInfo);
    yield call(reloadSpace);
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function syncGroup(spaceInfo: SpaceInfo) {
  const groupInfo = spaceInfo.item as IGroupInfo;
  const context = ServiceLocator.getAngularActionContext();
  return ServiceLocator.get<ActiveDirectoryGroupAction>('ActiveDirectoryGroupAction')
    .showSyncAdGroupDialog(groupInfo, context.scope);
}

export function* handleSyncGroup(action: GroupSyncAction): SagaIterator {
  try {
    const spaceInfo = yield select(selectSpaceInfo);
    yield call(syncGroup, spaceInfo);
    yield call(reloadSpace);
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function syncGroups(groups: IGroupInfo[]) {
  const context = ServiceLocator.getAngularActionContext();
  return ServiceLocator.get<ActiveDirectoryGroupAction>('ActiveDirectoryGroupAction')
    .showSyncAdGroupsDialog(groups, context.scope);
}

export function* handleSyncGroups(action: GroupSyncAction): SagaIterator {
  try {
    yield call(syncGroups, action.groups);
    yield call(reloadSpace);
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function updateMinSiteRole(groups: IGroupInfo[]) {
  const context = ServiceLocator.getAngularActionContext();
  return ServiceLocator.get<UpdateGroupsMinimumSiteRoleAction>('UpdateGroupsMinimumSiteRoleAction')
    .showUpdateGroupsMinimumSiteRoleDialog(Set.fromIdItemArray(groups), context);
}

export function* handleMinSiteRoleUpdate(action: GroupMinimumSiteRoleUpdateAction): SagaIterator {
  try {
    yield call(updateMinSiteRole, action.groups);
    yield call(reloadSpace);
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function updateLocalGroupGrantLicenseMode(groups: IGroupInfo[]) {
  const context = ServiceLocator.getAngularActionContext();
  return ServiceLocator.get<UpdateLocalGroupGrantLicenseModeAction>('UpdateLocalGroupGrantLicenseModeAction')
     .showUpdateGrantLicenseModeDialog(Set.fromIdItemArray(groups), context.scope);
}

export function* handleLocalGroupGrantLicenseModeUpdate(action: LocalGroupLicenseUponLoginUpdateAction): SagaIterator {
  try {
    yield call(updateLocalGroupGrantLicenseMode, action.groups);
    yield call(reloadSpace);
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function deleteGroups(groups: IGroupInfo[]) {
  return ServiceLocator.get<DeleteGroupAction>('DeleteGroupAction')
    .showDeleteGroupsDialog(Set.fromIdItemArray(groups));
}

export function* handleDeleteGroups(action: GroupsDeleteAction): SagaIterator {
  try {
    yield call(deleteGroups, action.groups);
    yield call(reloadSpace);
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function addGroup() {
  const context = ServiceLocator.getAngularActionContext();
  const isServerDomainFamilyActiveDirectory: boolean = ServiceLocator.get<ServerService>('ServerService')
    .isServerDomainFamilyActiveDirectory();
  if (isServerDomainFamilyActiveDirectory) {
    return ServiceLocator.get<AddGroupsOptionsAction>('AddGroupsOptionsAction')
      .showAddGroupsOptionsDialog(context);
  } else {
    return ServiceLocator.get<CreateGroupDialogAction>('CreateGroupDialogAction')
      .showCreateGroupDialog(context.scope);
  }
}

export function* handleAddGroup(action: GroupAddAction): SagaIterator {
  try {
    yield call(addGroup);
    yield call(reloadSpace);
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function* rootGroupMenuActionSaga(): SagaIterator {
  yield takeEvery(GroupAddUsers, handleAddUsers);
  yield takeEvery(GroupRemoveUsers, handleRemoveUsers);
  yield takeEvery(SyncAdGroup, handleSyncGroup);
  yield takeEvery(SyncAdGroups, handleSyncGroups);
  yield takeEvery(UpdateGroupsMinimumSiteRole, handleMinSiteRoleUpdate);
  yield takeEvery(UpdateLocalGroupsLicenseUponLogin, handleLocalGroupGrantLicenseModeUpdate);
  yield takeEvery(DeleteGroups, handleDeleteGroups);
  yield takeEvery(AddGroup, handleAddGroup);
}
